import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../services/api.service';

interface AnalyticsSummary {
  period_days: number;
  since: string;
  usd_to_pkr: number;
  calls: {
    total: number;
    outbound: number;
    inbound: number;
    by_status: Record<string, number>;
  };
  leads: {
    converted: number;
    interested: number;
    not_interested: number;
    by_disposition: Record<string, { label: string; count: number }>;
  };
  cost: {
    total_tokens: number;
    total_usd: number;
    total_pkr: number;
    avg_usd_per_call: number;
    avg_pkr_per_call: number;
    total_duration_seconds: number;
  };
  recent_calls: Array<{
    id: string;
    campaign_name: string;
    direction: string;
    phone?: string | null;
    status: string;
    duration_seconds: number;
    total_tokens: number;
    cost_usd: number;
    cost_pkr: number;
    started_at?: string | null;
  }>;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page">
      <div class="top">
        <div>
          <h1>Costs &amp; conversions</h1>
          <p>Tokens, USD / PKR spend, outbound vs inbound, and conversion tags</p>
        </div>
        <div class="controls">
          <label>
            Period
            <select [(ngModel)]="days" (ngModelChange)="load()">
              <option [ngValue]="7">7 days</option>
              <option [ngValue]="30">30 days</option>
              <option [ngValue]="90">90 days</option>
            </select>
          </label>
          <button (click)="load()" [disabled]="loading()">{{ loading() ? 'Loading…' : 'Refresh' }}</button>
        </div>
      </div>

      <div class="error" *ngIf="error()">{{ error() }}</div>

      <ng-container *ngIf="data() as d">
        <section class="hero">
          <div>
            <span>Total spend (PKR)</span>
            <strong>Rs {{ d.cost.total_pkr | number:'1.0-2' }}</strong>
            <small>\${{ d.cost.total_usd | number:'1.2-4' }} · rate {{ d.usd_to_pkr }}</small>
          </div>
          <div>
            <span>Tokens</span>
            <strong>{{ d.cost.total_tokens | number }}</strong>
            <small>avg Rs {{ d.cost.avg_pkr_per_call | number:'1.0-2' }} / call</small>
          </div>
          <div>
            <span>Calls</span>
            <strong>{{ d.calls.total }}</strong>
            <small>{{ d.calls.outbound }} out · {{ d.calls.inbound }} in</small>
          </div>
          <div>
            <span>Converted</span>
            <strong>{{ d.leads.converted }}</strong>
            <small>{{ d.leads.interested }} interested · {{ d.leads.not_interested }} not interested</small>
          </div>
        </section>

        <section class="grid">
          <div class="card">
            <h2>Call volume</h2>
            <div class="stat"><span>Outbound made</span><strong>{{ d.calls.outbound }}</strong></div>
            <div class="stat"><span>Inbound received</span><strong>{{ d.calls.inbound }}</strong></div>
            <div class="stat"><span>Talk time</span><strong>{{ minutes(d.cost.total_duration_seconds) }} min</strong></div>
            <div class="chips">
              <span *ngFor="let s of statusEntries(d)">{{ s[0] }} · {{ s[1] }}</span>
            </div>
          </div>
          <div class="card">
            <h2>Dispositions</h2>
            <div class="stat" *ngFor="let item of dispositionEntries(d)">
              <span>{{ item.label }}</span><strong>{{ item.count }}</strong>
            </div>
            <p class="hint" *ngIf="!dispositionEntries(d).length">No disposition tags in this period yet.</p>
          </div>
          <div class="card">
            <h2>Costing model</h2>
            <p class="hint">
              Gemini 2.5 Flash Native Audio (Live). Each turn’s tokens are summed
              (context is re-billed per turn). PKR = USD × {{ d.usd_to_pkr }}.
              Cloud Console remains the final bill.
            </p>
            <div class="stat"><span>Avg USD / call</span><strong>\${{ d.cost.avg_usd_per_call | number:'1.4-4' }}</strong></div>
            <div class="stat"><span>Avg PKR / call</span><strong>Rs {{ d.cost.avg_pkr_per_call | number:'1.2-2' }}</strong></div>
          </div>
        </section>

        <section class="card">
          <h2>Recent calls</h2>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Dir</th>
                <th>Status</th>
                <th>Tokens</th>
                <th>USD</th>
                <th>PKR</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let c of d.recent_calls">
                <td>{{ c.started_at | date:'short' }}</td>
                <td>
                  <strong>{{ c.phone || '—' }}</strong>
                  <small>{{ c.campaign_name }}</small>
                </td>
                <td>{{ c.direction }}</td>
                <td>{{ c.status }}</td>
                <td>{{ c.total_tokens | number }}</td>
                <td>\${{ c.cost_usd | number:'1.4-4' }}</td>
                <td>Rs {{ c.cost_pkr | number:'1.2-2' }}</td>
              </tr>
            </tbody>
          </table>
          <p class="hint" *ngIf="!d.recent_calls.length">No calls in this period.</p>
        </section>
      </ng-container>
    </div>
  `,
  styles: [
    `
      .page { display: grid; gap: 16px; }
      .top, .controls, .hero, .stat, .chips { display: flex; }
      .top { justify-content: space-between; gap: 16px; align-items: flex-start; }
      h1, h2, p { margin: 0; }
      h1 { font-family: Georgia, serif; }
      p, small, .hint { color: #64748b; }
      .controls { gap: 10px; align-items: end; }
      label { display: grid; gap: 4px; font-size: 12px; }
      select, button {
        border: 0; border-radius: 10px; padding: 10px 12px; font: inherit;
      }
      select { border: 1px solid #cbd5e1; background: white; }
      button { background: #0f766e; color: white; cursor: pointer; }
      .hero {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
      }
      .hero > div, .card, .error {
        background: white;
        border-radius: 16px;
        padding: 16px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      }
      .hero > div { display: grid; gap: 4px; }
      .hero span { color: #64748b; font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; }
      .hero strong { font-size: 1.6rem; color: #0f172a; }
      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
      .card { display: grid; gap: 10px; align-content: start; }
      .stat { justify-content: space-between; gap: 12px; padding: 6px 0; border-top: 1px solid #e2e8f0; }
      .chips { flex-wrap: wrap; gap: 6px; }
      .chips span {
        background: #f1f5f9; border-radius: 999px; padding: 4px 10px; font-size: 12px;
      }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th, td { text-align: left; padding: 10px 8px; border-top: 1px solid #e2e8f0; vertical-align: top; }
      td small { display: block; color: #64748b; }
      .error { color: #b91c1c; background: #fef2f2; }
      @media (max-width: 900px) {
        .hero, .grid { grid-template-columns: 1fr 1fr; }
        .top { flex-direction: column; }
      }
      @media (max-width: 600px) {
        .hero, .grid { grid-template-columns: 1fr; }
      }
    `,
  ],
})
export class CostsPage implements OnInit {
  data = signal<AnalyticsSummary | null>(null);
  loading = signal(false);
  error = signal('');
  days = 30;

  constructor(private readonly api: ApiService) {}

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      this.data.set(await this.api.analyticsSummary(this.days));
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Could not load analytics');
    } finally {
      this.loading.set(false);
    }
  }

  minutes(seconds: number): number {
    return Math.round((seconds || 0) / 60);
  }

  statusEntries(d: AnalyticsSummary): Array<[string, number]> {
    return Object.entries(d.calls.by_status || {});
  }

  dispositionEntries(d: AnalyticsSummary): Array<{ label: string; count: number }> {
    return Object.values(d.leads.by_disposition || {});
  }
}
